import clsx from 'clsx';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { InView } from 'react-intersection-observer';

import { PrimaryButton, ProButton } from '@components/Button/variants';
import EmailLeadCapture from '@components/EmailLeadCapture';
import useBlueConicBottomStickyBannerDialogue from '@hooks/useBlueConicBottomStickyBannerDialogue';
import useEventTracking from '@hooks/useEventTracking';
import useScrollPosition from '@hooks/useScrollPosition';
import useWindowSize from '@hooks/useWindowSize';
import { Theme } from '@interfaces/global';
import { subscribe_path } from '@routes';
import { ClickTarget, ImpressionElement, TrackingLocation, click, impression } from '@tracking';
import { getDialogueNameByVariantId } from '@utils/blueconic/utils';
import { WIDTH_SM } from '@utils/constants';
import { addParamToUrl } from '@utils/queryStringHelper';

import { BottomBannerProps } from '.';

type BottomBannerContentProps = Omit<BottomBannerProps, 'env' | 'isGdprRequired' | 'isWebview'>;

const SCROLL_THRESHOLD = 680;

const BottomBannerContent = ({
  articleId,
  buttonText,
  formLocation,
  headline,
  hideUntilScroll,
  isLoggedIn,
  isPro,
  leadDetails,
  leadType,
  submitLink,
  theme,
}: BottomBannerContentProps) => {
  const { blueConicProps } = useBlueConicBottomStickyBannerDialogue();
  const BannerElement = useRef(null);
  const footerElement = useRef<HTMLElement>(null);
  const [showBanner, setShowBanner] = useState<boolean>(!hideUntilScroll);
  const { width: windowWidth, height: windowHeight } = useWindowSize();
  const { trackEvent, Track } = useEventTracking({
    location: TrackingLocation.BottomBanner,
    contentId: blueConicProps?.variantId,
    externalSourceName: getDialogueNameByVariantId(blueConicProps?.variantId),
  });
  const Button = isPro ? ProButton : PrimaryButton;

  const subscribeVia =
    blueConicProps?.variantId != null
      ? `bc_variant_${getDialogueNameByVariantId(blueConicProps.variantId)}`
      : null;

  const subscribePathWithVia = subscribe_path(
    subscribeVia ? { subscribe_via: subscribeVia } : undefined
  );

  let subscribeRedirectUri = subscribePathWithVia;

  if (blueConicProps?.redirectUri) {
    subscribeRedirectUri = subscribeVia
      ? addParamToUrl(blueConicProps.redirectUri, 'subscribe_via', subscribeVia)
      : blueConicProps.redirectUri;
  }

  const showBannerBasedOnScroll = useCallback(
    (currPosY: number) => {
      const absoluteYPos = Math.abs(currPosY);
      const position = windowHeight + absoluteYPos;
      const shouldShowBanner =
        position < footerElement.current?.offsetTop &&
        ((hideUntilScroll && absoluteYPos > SCROLL_THRESHOLD) || !hideUntilScroll);

      setShowBanner(shouldShowBanner);
    },
    [hideUntilScroll, windowHeight]
  );

  useScrollPosition({
    effect: ({ currPos }: { currPos: { x: number; y: number } }) =>
      showBannerBasedOnScroll(currPos.y),
    deps: [footerElement.current],
    wait: 100,
  });

  useEffect(() => {
    footerElement.current = document.getElementById('ti-footer');
    if (window.scrollY) showBannerBasedOnScroll(window.scrollY);
  }, [showBannerBasedOnScroll]);

  return (
    <Track>
      {showBanner && (
        <InView
          triggerOnce={true}
          onChange={(inView) => {
            inView &&
              trackEvent(
                impression({
                  impressionElement: ImpressionElement.BottomBanner,
                })
              );
          }}
        >
          <div
            className={clsx(
              'min-h-56 w-full px-16 py-8 dark:bg-[var(--light-grayscale-charcoal)]',
              {
                'bg-grayscale-charcoal': theme === Theme.Dark,
                'bg-white sm:bg-grayscale-charcoal': theme === Theme.Light,
              }
            )}
            data-testid="lock-lead-capture-form"
          >
            <div
              className="m-auto flex flex-col items-center justify-center md:flex-row md:gap-16 lg:gap-48"
              ref={BannerElement}
            >
              <div
                className={clsx(
                  'my-8 max-w-full text-center font-suisse-neue text-body-md font-normal md:my-0',
                  {
                    'text-white': theme === Theme.Dark,
                    'text-black sm:text-white': theme === Theme.Light,
                  }
                )}
              >
                {blueConicProps?.title || headline}
              </div>
              <div className="flex w-full items-center sm:w-auto">
                {isLoggedIn ? (
                  <Button
                    as="link"
                    size="small"
                    href={subscribeRedirectUri || submitLink}
                    className="w-auto cursor-pointer touch-manipulation max-sm:w-auto"
                    onClickCapture={() =>
                      trackEvent(
                        click({
                          clickTarget: ClickTarget.SubmitButton,
                          clickText: blueConicProps?.loggedInButtonText || buttonText,
                        })
                      )
                    }
                  >
                    {blueConicProps?.loggedInButtonText || buttonText}
                  </Button>
                ) : (
                  <EmailLeadCapture
                    articleId={articleId}
                    buttonClassName="w-full sm:w-auto"
                    buttonText={blueConicProps?.buttonText || buttonText}
                    formClassName="flex items-start sm:items-center flex-col sm:flex-row"
                    formLocation={formLocation}
                    gdprClassName="mb-8 sm:mb-0 sm:mr-32"
                    inputClassName="w-full sm:w-[240px] dark:bg-[var(--light-grayscale-charcoal)]"
                    isVertical={windowWidth < WIDTH_SM}
                    leadDetails={leadDetails}
                    leadType={blueConicProps?.leadType || leadType}
                    redirectToOnSuccess={subscribeRedirectUri}
                    theme={Theme.Dark}
                  />
                )}
              </div>
            </div>
          </div>
        </InView>
      )}
    </Track>
  );
};

export default BottomBannerContent;
